import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const QMAPPage = () => {
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    employer: '',
    jobTitle: '',
    preferredClassDate: '',
    preferredTime: '',
    previousTraining: '',
    specialRequirements: ''
  });

  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d590homisj01hmefr0eg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString()
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Enrollment submitted successfully! We will contact you within 48 hours to confirm your class schedule.' });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          dateOfBirth: '',
          employer: '',
          jobTitle: '',
          preferredClassDate: '',
          preferredTime: '',
          previousTraining: '',
          specialRequirements: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: 'Submission failed. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            QMAP Training Program
          </motion.h1>
          <motion.p
            className="text-white/80 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> QMAP
          </motion.p>
        </div>
      </section>

      {!showEnrollmentForm ? (
        <>
          {/* Program Overview */}
          <section className="container mx-auto px-6 py-16">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">What is QMAP Certification?</h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  QMAP (Qualified Medication Administration Personnel) certification enables unlicensed healthcare workers to safely administer medications in residential care facilities, group homes, and assisted living settings throughout Colorado.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
                  <div className="w-16 h-16 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-time-line text-3xl text-[rgba(75,162,75)]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">16-Hour Course</h3>
                  <p className="text-gray-600">Comprehensive training covering medication administration, safety protocols, and legal requirements.</p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
                  <div className="w-16 h-16 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-award-line text-3xl text-[rgba(75,162,75)]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">State Certified</h3>
                  <p className="text-gray-600">Meets all Colorado Department of Public Health requirements for QMAP certification.</p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
                  <div className="w-16 h-16 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-group-line text-3xl text-[rgba(75,162,75)]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Instructors</h3>
                  <p className="text-gray-600">Taught by experienced psychiatric healthcare professionals with years of clinical practice.</p>
                </div>
              </div>

              {/* Course Content */}
              <div className="bg-white rounded-xl shadow-lg p-10 mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Course Curriculum</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[rgba(75,162,75,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-[rgba(75,162,75)]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Medication Basics</h4>
                      <p className="text-gray-600 text-sm">Understanding drug classifications, routes of administration, and medication forms</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[rgba(75,162,75,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-[rgba(75,162,75)]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Safety Protocols</h4>
                      <p className="text-gray-600 text-sm">Five Rights of medication administration and error prevention strategies</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[rgba(75,162,75,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-[rgba(75,162,75)]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Documentation</h4>
                      <p className="text-gray-600 text-sm">Proper record-keeping, medication logs, and incident reporting</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[rgba(75,162,75,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-[rgba(75,162,75)]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Legal Requirements</h4>
                      <p className="text-gray-600 text-sm">Colorado regulations, scope of practice, and professional responsibilities</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[rgba(75,162,75,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-[rgba(75,162,75)]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Side Effects & Interactions</h4>
                      <p className="text-gray-600 text-sm">Recognizing adverse reactions and understanding drug interactions</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[rgba(75,162,75,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-[rgba(75,162,75)]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Practical Skills</h4>
                      <p className="text-gray-600 text-sm">Hands-on practice with medication administration techniques</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Class Schedule & Pricing */}
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-[rgba(75,162,75,0.1)] to-[rgba(75,162,75,0.15)] rounded-xl p-8 border border-[rgba(75,162,75,0.3)]">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Class Schedule</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-calendar-line text-2xl text-[rgba(75,162,75)]"></i>
                      <div>
                        <p className="font-semibold text-gray-900">Monthly Classes</p>
                        <p className="text-sm text-gray-600">Second Saturday of each month</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-time-line text-2xl text-[rgba(75,162,75)]"></i>
                      <div>
                        <p className="font-semibold text-gray-900">One-Day Format</p>
                        <p className="text-sm text-gray-600">10:00 AM - 6:00 PM</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-map-pin-line text-2xl text-[rgba(75,162,75)]"></i>
                      <div>
                        <p className="font-semibold text-gray-900">Location</p>
                        <p className="text-sm text-gray-600">2821-2851 S Parker RD, Suite 1-1030, Aurora, CO 80014</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Course Investment</h3>
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-[rgba(75,162,75)] mb-2">$120</div>
                    <p className="text-gray-600">Per participant</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)]"></i>
                      <span>All course materials included</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)]"></i>
                      <span>State certification exam</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)]"></i>
                      <span>Certificate upon completion</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)]"></i>
                      <span>Group discounts available</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="bg-white rounded-xl shadow-lg p-10 mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Enrollment Requirements</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Prerequisites</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-[rgba(75,162,75)] mt-1"></i>
                        <span>Must be 18 years or older</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-[rgba(75,162,75)] mt-1"></i>
                        <span>High school diploma or GED</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">What to Bring</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-[rgba(75,162,75)] mt-1"></i>
                        <span>Valid photo identification</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-[rgba(75,162,75)] mt-1"></i>
                        <span>Notebook and writing materials</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-[rgba(75,162,75)] mt-1"></i>
                        <span>Lunch (or purchase nearby)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-[rgba(75,162,75)] mt-1"></i>
                        <span>Comfortable clothing for practical exercises</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-[rgba(75,162,75)] to-[rgba(75,162,75,0.9)] rounded-xl p-10 text-white text-center">
                <h3 className="text-3xl font-bold mb-4">Ready to Get Certified?</h3>
                <p className="text-white/80 mb-8 text-lg max-w-2xl mx-auto">
                  Enroll in our next QMAP training class and advance your healthcare career. Limited spots available per session.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/appointment"
                    className="px-8 py-3 bg-[rgba(75,162,75)] text-white rounded-md hover:bg-[rgba(75,162,75,0.9)] transition-colors font-medium whitespace-nowrap inline-flex items-center justify-center gap-2"
                  >
                    <i className="ri-calendar-line"></i>
                    Enroll in Training
                  </a>
                  <a
                    href="/payment-setup"
                    className="px-8 py-3 bg-white text-[rgba(75,162,75)] border-2 border-[rgba(75,162,75)] rounded-md hover:bg-[rgba(75,162,75)] hover:text-white transition-colors font-medium whitespace-nowrap inline-flex items-center justify-center gap-2"
                  >
                    <i className="ri-secure-payment-line"></i>
                    Set Up Payment
                  </a>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => {
                setShowEnrollmentForm(false);
                setSubmitStatus(null);
              }}
              className="mb-8 text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Program Information
            </button>

            <div className="bg-white rounded-xl shadow-lg p-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">QMAP Training Enrollment</h2>
              <p className="text-gray-600 mb-8">Complete this form to reserve your spot in our next QMAP certification class.</p>

              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    <i className={`${submitStatus.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-xl`}></i>
                    <span>{submitStatus.message}</span>
                  </div>
                </div>
              )}

              <form id="qmap-enrollment-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Home Address *</label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    required
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current Employer *</label>
                    <input
                      type="text"
                      name="employer"
                      required
                      value={formData.employer}
                      onChange={(e) => setFormData({ ...formData, employer: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title *</label>
                    <input
                      type="text"
                      name="jobTitle"
                      required
                      value={formData.jobTitle}
                      onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Class Date *</label>
                    <input
                      type="date"
                      name="preferredClassDate"
                      required
                      value={formData.preferredClassDate}
                      onChange={(e) => setFormData({ ...formData, preferredClassDate: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Time *</label>
                    <select
                      name="preferredTime"
                      required
                      value={formData.preferredTime}
                      onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    >
                      <option value="">Select Time</option>
                      <option value="Weekend (Saturday)">Weekend (Saturday)</option>
                      <option value="Weekday Morning">Weekday Morning</option>
                      <option value="Weekday Evening">Weekday Evening</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Previous QMAP or Medical Training</label>
                  <textarea
                    name="previousTraining"
                    rows={3}
                    maxLength={500}
                    value={formData.previousTraining}
                    onChange={(e) => setFormData({ ...formData, previousTraining: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="List any relevant certifications or training (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.previousTraining.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Special Requirements or Accommodations</label>
                  <textarea
                    name="specialRequirements"
                    rows={3}
                    maxLength={500}
                    value={formData.specialRequirements}
                    onChange={(e) => setFormData({ ...formData, specialRequirements: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="Any accessibility needs or special requests (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.specialRequirements.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[rgba(75,162,75)] text-white py-4 rounded-lg font-semibold hover:bg-[rgba(75,162,75,0.9)] transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Submit Enrollment
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default QMAPPage;
